/**
 * Tournament match runtime — async bracket combat hooks into legacy-app.
 */
import {
  finishTournamentRun,
  pollTournamentMatch,
  reportTournamentProgress,
} from "./api";
import { showTournamentWaitingScreen, tryJoinTournamentMatch } from "./screen";
import type { TournamentDetail } from "./types";
import { tournamentUsesLevelMetric } from "./types";

export type TournamentRuntimeHooks = {
  isSignedIn: () => boolean;
  startModeRun: (modeId: string, opts?: { levelIndex?: number | null }) => void | Promise<void>;
  endRunQuiet: () => void;
  getScore: () => number;
  getProgressionLevel: () => number;
  getPlayElapsedSec: () => number;
  setDotsBalance: (balance: number) => void;
  setReturnBtnLabel: (label: string) => void;
  showTournamentResultOverlay: (html: string) => void;
  hideTournamentResultOverlay: () => void;
  returnToTournamentDetail: () => void;
  persistRunBestIfAny: () => void;
};

type Phase = "idle" | "waiting" | "playing" | "result";

const OPPONENT_REVEAL_MS = 5000;

let hooks: TournamentRuntimeHooks | null = null;
let phase: Phase = "idle";
let matchId: string | null = null;
let tournamentDetail: TournamentDetail | null = null;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let progressTimer: ReturnType<typeof setInterval> | null = null;
let waitPollTimer: ReturnType<typeof setInterval> | null = null;
let resultPollTimer: ReturnType<typeof setInterval> | null = null;
let revealTimer: ReturnType<typeof setTimeout> | null = null;
let opponentFinished = false;
let opponentScore: number | null = null;
let opponentName: string | null = null;
let modeId = "normal";
let levelIndex: number | null = null;

export function mountTournamentRuntime(h: TournamentRuntimeHooks): void {
  hooks = h;
}

export function isTournamentActive(): boolean {
  return phase !== "idle";
}

export function isTournamentPlaying(): boolean {
  return phase === "playing";
}

export function getTournamentOpponentScore(): number | null {
  return opponentScore;
}

export function isTournamentOpponentFinished(): boolean {
  return opponentFinished;
}

function stopPollers() {
  if (pollTimer) clearInterval(pollTimer);
  if (progressTimer) clearInterval(progressTimer);
  pollTimer = null;
  progressTimer = null;
}

function clearWaitPoll() {
  if (waitPollTimer) clearInterval(waitPollTimer);
  waitPollTimer = null;
}

function clearResultPoll() {
  if (resultPollTimer) clearInterval(resultPollTimer);
  resultPollTimer = null;
}

function clearRevealTimer() {
  if (revealTimer) clearTimeout(revealTimer);
  revealTimer = null;
}

function matchProgressValue(): number {
  if (!hooks) return 0;
  if (tournamentUsesLevelMetric(modeId)) return hooks.getProgressionLevel();
  return hooks.getScore();
}

function startPollers() {
  stopPollers();
  if (!matchId) return;
  pollTimer = setInterval(() => {
    void (async () => {
      try {
        const data = await pollTournamentMatch(matchId!);
        opponentScore = data.opponentScore != null ? Number(data.opponentScore) : null;
        opponentFinished = !!data.opponentFinished;
        if (typeof data.opponentName === "string" && data.opponentName) {
          opponentName = data.opponentName;
        }
        if (data.status === "tie_pending" && phase === "playing") {
          showTieRestartUi(String(data.tieRestartAt || ""));
        }
      } catch {
        /* ignore transient poll errors */
      }
    })();
  }, 2500);
  progressTimer = setInterval(() => {
    if (phase !== "playing" || !matchId) return;
    void reportTournamentProgress(matchId, matchProgressValue()).catch(() => {});
  }, 4000);
}

function showTieRestartUi(tieRestartAt: string) {
  const restartMs = tieRestartAt ? new Date(tieRestartAt).getTime() - Date.now() : 10000;
  hooks?.showTournamentResultOverlay(
    `<p class="tournamentResultTitle">TIE</p><p class="tournamentResultSub">Restarting Match...</p><p class="tournamentResultSub">${Math.max(0, Math.ceil(restartMs / 1000))}s</p>`,
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    revealTimer = setTimeout(() => {
      revealTimer = null;
      resolve();
    }, ms);
  });
}

async function revealOpponentAndStart(): Promise<void> {
  if (!hooks || !matchId) return;
  const label = opponentName?.trim() || "Opponent";
  showTournamentWaitingScreen(true, `VS ${label}`);
  await sleep(OPPONENT_REVEAL_MS);
  if (phase !== "waiting" || !matchId) return;
  showTournamentWaitingScreen(false);
  phase = "playing";
  startPollers();
  await hooks.startModeRun(modeId, { levelIndex });
}

function returnToDetailAfter(ms: number) {
  window.setTimeout(() => {
    hooks?.hideTournamentResultOverlay();
    hooks?.endRunQuiet();
    phase = "idle";
    matchId = null;
    opponentName = null;
    hooks?.returnToTournamentDetail();
  }, ms);
}

function showFinalOutcome(result: Record<string, unknown>) {
  const outcome = String(result.outcome || "");
  if (result.dotsBalance != null && Number.isFinite(Number(result.dotsBalance))) {
    hooks?.setDotsBalance(Math.max(0, Math.floor(Number(result.dotsBalance))));
  }
  let html = "";
  if (outcome === "advanced") {
    if (result.tournamentEnded) {
      html = `<p class="tournamentResultTitle">YOU WON</p>`;
    } else {
      html = `<p class="tournamentResultTitle">YOU ADVANCED</p>`;
    }
    if (Number(result.dotsEarned) > 0) {
      html += `<p class="tournamentResultSub">+${result.dotsEarned} Dots</p>`;
    }
    if (!result.tournamentEnded) {
      if (result.nextOpponentName) {
        html += `<p class="tournamentResultSub">Next opponent: ${result.nextOpponentName}</p>`;
      } else if (result.nextMatchPending) {
        html += `<p class="tournamentResultSub">Waiting for next opponent...</p>`;
      }
    }
  } else if (outcome === "eliminated") {
    html = `<p class="tournamentResultTitle">ELIMINATED</p>`;
    if (Number(result.dotsEarned) > 0) {
      html += `<p class="tournamentResultSub">+${result.dotsEarned} Dots (runner-up)</p>`;
    }
  } else {
    html = `<p class="tournamentResultTitle">RUN ENDED</p>`;
  }
  hooks?.showTournamentResultOverlay(html);
  returnToDetailAfter(5000);
}

function pollUntilMatchResolved(initial?: Record<string, unknown>) {
  clearResultPoll();
  hooks?.showTournamentResultOverlay(
    `<p class="tournamentResultTitle">WAITING</p><p class="tournamentResultSub">Opponent still playing...</p>`,
  );
  const applyResolved = (data: Record<string, unknown>) => {
    const status = String(data.status || "");
    const outcome = String(data.outcome || "");
    if (status === "tie_pending" || outcome === "tie") {
      clearResultPoll();
      hooks?.showTournamentResultOverlay(
        `<p class="tournamentResultTitle">TIE</p><p class="tournamentResultSub">Restarting Match...</p>`,
      );
      window.setTimeout(() => {
        hooks?.hideTournamentResultOverlay();
        if (tournamentDetail && matchId) void beginTournamentMatch(tournamentDetail, matchId);
      }, 10000);
      return true;
    }
    if (status === "completed" || status === "forfeit" || outcome === "advanced" || outcome === "eliminated") {
      clearResultPoll();
      showFinalOutcome({
        outcome: outcome === "advanced" || outcome === "eliminated" ? outcome : data.outcome,
        dotsEarned: data.dotsEarned ?? initial?.dotsEarned ?? 0,
        nextOpponentName: data.nextOpponentName ?? initial?.nextOpponentName ?? null,
        nextMatchPending: data.nextMatchPending ?? initial?.nextMatchPending ?? false,
        tournamentEnded: data.tournamentEnded ?? initial?.tournamentEnded ?? false,
        dotsBalance: data.dotsBalance ?? initial?.dotsBalance ?? null,
      });
      return true;
    }
    return false;
  };
  if (initial && applyResolved(initial)) return;
  resultPollTimer = setInterval(() => {
    if (!matchId) {
      clearResultPoll();
      return;
    }
    void (async () => {
      try {
        const data = await pollTournamentMatch(matchId!);
        opponentScore = data.opponentScore != null ? Number(data.opponentScore) : null;
        opponentFinished = !!data.opponentFinished;
        applyResolved(data);
      } catch {
        /* keep waiting */
      }
    })();
  }, 2000);
}

export async function beginTournamentMatch(
  detail: TournamentDetail,
  mid: string,
): Promise<void> {
  if (!hooks?.isSignedIn()) return;
  clearWaitPoll();
  clearResultPoll();
  clearRevealTimer();
  stopPollers();
  tournamentDetail = detail;
  matchId = mid;
  phase = "waiting";
  opponentFinished = false;
  opponentScore = null;
  const matchRow = detail.matches.find((m) => m.id === mid);
  if (matchRow?.viewerSlot === 1) {
    opponentName = matchRow.player2?.displayName ?? null;
  } else if (matchRow?.viewerSlot === 2) {
    opponentName = matchRow.player1?.displayName ?? null;
  } else {
    opponentName = null;
  }
  hooks.setReturnBtnLabel("Return");
  showTournamentWaitingScreen(true, "Waiting for opponent...");

  try {
    const join = await tryJoinTournamentMatch(mid);
    modeId = join.modeId;
    levelIndex = join.levelIndex;
    if (join.opponentName) opponentName = join.opponentName;
    if (!join.opponentJoined) {
      waitPollTimer = setInterval(() => {
        void (async () => {
          try {
            const data = await pollTournamentMatch(mid);
            if (typeof data.opponentName === "string" && data.opponentName) {
              opponentName = data.opponentName;
            }
            if (data.status === "active" || data.opponentJoined) {
              clearWaitPoll();
              await revealOpponentAndStart();
            }
          } catch {
            /* keep waiting */
          }
        })();
      }, 2000);
      return;
    }
    await revealOpponentAndStart();
  } catch (e) {
    phase = "idle";
    clearWaitPoll();
    clearRevealTimer();
    showTournamentWaitingScreen(false);
    hooks.returnToTournamentDetail();
    throw e;
  }
}

export async function onTournamentPlayerDeath(): Promise<void> {
  if (!hooks || phase !== "playing" || !matchId) return;
  stopPollers();
  phase = "result";
  hooks.showTournamentResultOverlay(`<p class="tournamentResultTitle">&nbsp;</p>`);
  hooks.persistRunBestIfAny();
  const score = matchProgressValue();
  try {
    const result = await finishTournamentRun(matchId, score);
    const outcome = String(result.outcome || "");
    if (outcome === "tie") {
      hooks.showTournamentResultOverlay(
        `<p class="tournamentResultTitle">TIE</p><p class="tournamentResultSub">Restarting Match...</p>`,
      );
      window.setTimeout(() => {
        hooks?.hideTournamentResultOverlay();
        if (tournamentDetail && matchId) void beginTournamentMatch(tournamentDetail, matchId);
      }, 10000);
      return;
    }
    if (outcome === "waiting" || outcome === "playing") {
      pollUntilMatchResolved(result);
      return;
    }
    showFinalOutcome(result);
  } catch {
    hooks.showTournamentResultOverlay(`<p class="tournamentResultTitle">RUN ENDED</p>`);
    returnToDetailAfter(3000);
  }
}

export function cancelTournamentRun(): void {
  stopPollers();
  clearWaitPoll();
  clearResultPoll();
  clearRevealTimer();
  showTournamentWaitingScreen(false);
  phase = "idle";
  matchId = null;
  tournamentDetail = null;
  opponentName = null;
  hooks?.hideTournamentResultOverlay();
}

export function drawTournamentOpponentHud(ctx: CanvasRenderingContext2D, canvasW: number): void {
  if (phase !== "playing") return;
  const label = opponentFinished
    ? `Opponent Score: ${opponentScore ?? "—"}`
    : `Opponent Score: ${opponentScore ?? "…"}`;
  ctx.save();
  ctx.font = "14px 'Press Start 2P', monospace";
  ctx.textAlign = "right";
  ctx.fillStyle = opponentFinished ? "#ff5555" : "#ffffff";
  ctx.strokeStyle = opponentFinished ? "#ff0000" : "#888888";
  ctx.lineWidth = 2;
  const x = canvasW - 16;
  const y = 28;
  ctx.strokeText(label, x, y);
  ctx.fillText(label, x, y);
  ctx.restore();
}
